import { useTranslation } from "@i18n";
import { useCallback, useEffect, useState } from "react";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { getReviewStatusForAllFileVersions } from "./ReviewsHelper";
import { statusCategories } from "@Features/portal-settings";
import OpenReviewsPageIcon from "@Assets/icons/OpenInViewer.svg";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import { getReviewUrl } from "@Features/projects/utils/projectUrls";
import type { ReactElement } from "react";
import type { GetReviewStatusForAllFileVersionsRequest } from "@ServerTypes/reviews/reviews.schema";
import type { ResourceStatusEntry } from "./ResourceStatusHistoryInReview";

/**
 * One review entry inside a file-version group. SERVER GAP:
 * `getReviewStatusForAllFileVersionsResponse` is `reviewStatusResponse`, whose `result` is
 * `z.unknown()`, so there is no server row type to flow. Shape taken from the `$project` in
 * `getReviewStatusForAllFileVersions` (reviews.service.ts).
 */
interface FileVersionReviewEntry {
  reviewId: string;
  reviewName?: string;
  reviewSerialId?: number;
  reviewStage?: string;
  reviewCurrentLevel?: { serial: number; title: string } | null;
  /** `$arrayElemAt: [statuses, -1]` — absent when the resource has no status yet. */
  status?: ResourceStatusEntry;
  reviewFlowName?: string;
  projectId: string;
  spaceId: string;
  fileVersionNumber?: string;
}

/** One `$group` bucket keyed by the rendered version label ("V3"). */
interface FileVersionReviewsGroup {
  _id?: string;
  fileVersionNumber?: string;
  reviews: FileVersionReviewEntry[];
}

interface AllFileVersionStatusInReviewsModalProps {
  /** Sent as `getReviewStatusForAllFileVersionsRequest.fileId` — a File `_id`, not a version id. */
  readonly fileId: GetReviewStatusForAllFileVersionsRequest["fileId"];
  readonly closeModal: () => void;
}

function AllFileVersionStatusInReviewsModal({ fileId, closeModal }: AllFileVersionStatusInReviewsModalProps) {
  const { t } = useTranslation();
  const [data, setData] = useState<FileVersionReviewsGroup[]>([]);

  const openReviewsPage = (_id: string, spaceId: string, projectId: string) => {
    const reviewsPageUrl = getReviewUrl(projectId, spaceId, _id);
    window.open(reviewsPageUrl, "_blank");
  };

  const getDropdownCategoryStyles = (category?: string | null) => {
    if (category === null) {
      return { backgroundColor: "#ecf3fe" };
    }

    const matchedCategory = statusCategories.find((record) => record.name === category);
    const backgroundColor = matchedCategory ? matchedCategory.backgroundHexcode : "#0006A2";

    const style = { backgroundColor };
    return style;
  };

  const fetchData = useCallback(async () => {
    const response = await getReviewStatusForAllFileVersions({ fileId });

    if (response.data.status) {
      setData(response.data.result as FileVersionReviewsGroup[]);
    }
  }, [fileId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const tooltipContentForReviewDetails = (
    name: string | undefined,
    serialId: number | undefined,
    reviewFlowName: string | undefined,
    currentReviewLevel: FileVersionReviewEntry["reviewCurrentLevel"],
    reviewStatus: string | undefined,
  ): ReactElement => (
    <div className="flex flex-col gap-2 w-[15.625rem]">
      <div className="flex flex-row items-baseline justify-between">
        <p>{t("Review ID")}</p>
        <p className="flex w-[6.25rem] self-start font-[var(--font-weight-600)]">{`RE-${serialId}`}</p>
      </div>

      <div className="flex flex-row items-baseline justify-between">
        <p>{t("Review Name")}</p>
        <p className="flex w-[6.25rem] self-start font-[var(--font-weight-600)]">{name}</p>
      </div>

      <div className="flex flex-row items-baseline justify-between">
        <p>{t("Review Stage")}</p>
        <p className="flex w-[6.25rem] self-start font-[var(--font-weight-600)]">{reviewStatus}</p>
      </div>

      {currentReviewLevel && (
        <div className="flex flex-row items-baseline justify-between">
          <p>{t("Current Level")}</p>
          <p className="flex w-[6.25rem] self-start font-[var(--font-weight-600)]">
            {`${currentReviewLevel.serial}-${currentReviewLevel.title}`}
          </p>
        </div>
      )}

      <div className="flex flex-row items-baseline justify-between">
        <p>{t("Review Flow Used")}</p>
        <p className="flex w-[6.25rem] self-start font-[var(--font-weight-600)]">{reviewFlowName}</p>
      </div>
    </div>
  );

  const header = () => (
    <div className="flex flex-row items-center justify-between w-full ">
      <p className="text-[1.375rem] font-[var(--font-weight-600)]">{t("All Reviews")}</p>
    </div>
  );

  const content = () => (
    <div className="flex flex-col gap-6 h-600 overflow-auto">
      {data.length
        ? data.map((record: FileVersionReviewsGroup, index: number) => (
            <div key={index} className="flex flex-col gap-2">
              <p className="text-base font-[var(--font-weight-600)]">{record.fileVersionNumber}</p>
              <div className="flex flex-col gap-4 pt-1 ps-2">
                {record.reviews.length ? (
                  record.reviews.map((review: FileVersionReviewEntry) => {
                    const {
                      reviewName,
                      reviewStage,
                      reviewSerialId,
                      reviewFlowName,
                      reviewId,
                      reviewCurrentLevel,
                      status,
                      spaceId,
                      projectId,
                    } = review;
                    // KNOWN BUG, preserved verbatim: `status` is `$arrayElemAt: [statuses, -1]`, so a
                    // resource with no status yet yields `undefined` and this deref throws, blanking
                    // the whole All-Reviews modal. Does the server read it? No — display-only. Fix:
                    // guard, which would add emitted code, so it is left alone.
                    const style = getDropdownCategoryStyles(status!.category);

                    return (
                      <ToolTipComponent
                        key={index}
                        showTipPointer
                        position="left"
                        content={tooltipContentForReviewDetails(
                          reviewName,
                          reviewSerialId,
                          reviewFlowName,
                          reviewCurrentLevel,
                          reviewStage,
                        )}
                      >
                        <div className="flex flex-row items-center justify-start gap-4">
                          <p className="whitespace-nowrap w-[7.5rem] overflow-hidden text-ellipsis text-sm font-[var(--font-weight-400)]">
                            {reviewName}
                          </p>
                          <div className="flex flex-row items-center justify-between w-full">
                            {status!._id ? (
                              <div
                                className="py-1.5 px-4 text-sm font-[var(--font-weight-400)] rounded text-white"
                                style={style}
                              >
                                {status!.name}
                              </div>
                            ) : (
                              "-"
                            )}
                            <div
                              onClick={() => openReviewsPage(reviewId, spaceId, projectId)}
                              className="flex flex-row items-center justify-center cursor-pointer"
                            >
                              <img src={OpenReviewsPageIcon} />
                            </div>
                          </div>
                        </div>
                      </ToolTipComponent>
                    );
                  })
                ) : (
                  <p className="text-sm font-[var(--font-weight-400)]">
                    {t("The file has not been sent for a review yet")}
                  </p>
                )}
              </div>
            </div>
          ))
        : undefined}
    </div>
  );

  return (
    <ModalStateDialogComponent
      isVisible
      allowModalPadding={false}
      width="31.25rem"
      onCancel={closeModal}
      headerTitle={header()}
      modelContent={content()}
    />
  );
}

export default AllFileVersionStatusInReviewsModal;
